import { jwtDecode } from 'jwt-decode';
import { CustomerAccessLevelEnum } from '@/app/core/auto-generated/api.service';
import { Permissions } from '@/app/core/auto-generated/permissions';

/**
 * Pure helpers shared by `SessionStore` and the `*hasPermission`
 * directive: decoding the Auth0 id-token claims into a {@link UserInfo}, the
 * access-token `permissions` claim into a lower-cased set, and the single
 * grant-check used everywhere (so the store's `can` and the directive agree on
 * semantics).
 *
 * Pure claim helpers let the store own the projection without re-deriving it.
 */

/** Auth0 custom-claim namespace the StatusNow tenant stamps onto the id token. */
const NAMESPACE = 'https://statusnow.com/';

type AccessTokenClaims = {
  aud?: unknown;
  permissions?: unknown;
  'https://statusnow.com/preview_employee'?: unknown;
  'https://statusnow.com/preview_permissions'?: unknown;
};

/** The current user's StatusNow claims, projected from the Auth0 id token. */
export class UserInfo {
  CustomerId: number | undefined;
  ViewCustomerIds: number[] = [];
  CreateLoadCustomerIds: number[] = [];
  BillCustomerIds: number[] = [];
  Email = '';
  IsASM = false;
  profilePicture = '';
  fullname = '';

  constructor(values?: Partial<UserInfo>) {
    Object.assign(this, values);
  }

  get HasAdditionalCreateCustomers(): boolean {
    return this.CreateLoadCustomerIds?.length > 0;
  }
}

/** Project the namespaced id-token claims into a {@link UserInfo}. */
export function toUserInfo(claims: Record<string, unknown>): UserInfo {
  return new UserInfo({
    CustomerId: readNumber(claims[NAMESPACE + 'customerId']),
    ViewCustomerIds: readNumberArray(claims[NAMESPACE + 'viewCustomerIds']),
    CreateLoadCustomerIds: readNumberArray(
      claims[NAMESPACE + 'createLoadCustomerIds']
    ),
    BillCustomerIds: readNumberArray(claims[NAMESPACE + 'billCustomerIds']),
    Email: readString(claims[NAMESPACE + 'email']),
    IsASM: readBoolean(claims[NAMESPACE + 'isASM']),
    profilePicture: readString(
      claims[NAMESPACE + 'profilePicture'],
      readString(claims['picture'])
    ),
    fullname: readString(claims[NAMESPACE + 'name']),
  });
}

/**
 * How many distinct customers an external (non-ASM) user can pick at the given
 * access level, read straight from the id-token claims. The list-filter roster
 * is exactly these ids — the backend's `GetUserCustomers` maps each access level
 * to the matching claim array — so this is the number of options a customer
 * filter would offer. Defaults to the `View` scope.
 */
export function accessibleCustomerCount(
  info: UserInfo,
  accessLevel: CustomerAccessLevelEnum = CustomerAccessLevelEnum.View
): number {
  const ids =
    accessLevel === CustomerAccessLevelEnum.Bill
      ? info.BillCustomerIds
      : accessLevel === CustomerAccessLevelEnum.Create
        ? info.CreateLoadCustomerIds
        : info.ViewCustomerIds;
  return new Set(ids).size;
}

/** Decode the lower-cased `permissions` claim from an access token. */
export function decodePermissions(token: string | null): ReadonlySet<string> {
  if (!token) {
    return new Set<string>();
  }

  try {
    const claims = jwtDecode<AccessTokenClaims>(token);
    const previewAudience = 'https://preview.statusnow.com/';
    const isPreview =
      claims.aud === previewAudience ||
      (Array.isArray(claims.aud) && claims.aud.includes(previewAudience));
    const grants = isPreview
      ? claims['https://statusnow.com/preview_employee'] === true
        ? claims['https://statusnow.com/preview_permissions']
        : undefined
      : claims.permissions;
    const permissions = Array.isArray(grants) ? grants : [];

    return new Set(
      permissions
        .filter(
          (permission): permission is string => typeof permission === 'string'
        )
        .map((permission) => permission.toLowerCase())
    );
  } catch {
    return new Set<string>();
  }
}

/**
 * Whether a permission is granted by the given (lower-cased) permission set.
 *
 * `dev:all` is a universal super-permission: it satisfies EVERY check (full
 * god-mode — a developer account can do anything in the UI, by product
 * decision). Any other permission requires exact (case-insensitive) membership.
 * The single source of grant semantics for the store and the `[hasPermission]`
 * directive.
 */
export function hasGrant(
  permissions: ReadonlySet<string>,
  permission: string
): boolean {
  if (permissions.has(Permissions.DEV_ALL)) {
    return true;
  }
  return permissions.has(permission.toLowerCase());
}

function readString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function readBoolean(value: unknown): boolean {
  return typeof value === 'boolean' ? value : false;
}

function readNumber(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : undefined;
  }

  return undefined;
}

function readNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((item) => {
    const numericValue = readNumber(item);
    return numericValue === undefined ? [] : [numericValue];
  });
}
